import type { RefObject } from "react";
import type { IColor } from "../../types";
import type { IDateLocale, IsoDate, Weekday } from "../../utils/date";

export type ICalendarProps = {
  /** The picked day; null for none. */
  value: IsoDate | null;
  onChange: (date: IsoDate) => void;
  /** The earliest day that can be picked, inclusive. */
  min?: IsoDate;
  /** The latest day that can be picked, inclusive. */
  max?: IsoDate;
  /** Defaults to the user's clock. Pass it to pin stories and tests. */
  today?: IsoDate;
  /** Defaults to the format locale's (Sunday in the US, Monday in Europe). */
  firstDay?: Weekday;
  /** Dots under days, coloured through the colour context. */
  markers?: ICalendarMarker[];
  /** Focus the days grid's tab stop on mount (a date picker opened from the keyboard). */
  autoFocus?: boolean;
  /** The first day of the shown month: on mount and whenever it changes. */
  onMonthChange?: (month: IsoDate) => void;
  className?: string;
};

export type ICalendarMarker = {
  date: IsoDate;
  color: IColor;
};

export enum CalendarView {
  Days = "days",
  Months = "months",
  Years = "years",
}

export type ICalendarContext = {
  value: IsoDate | null;
  today: IsoDate;
  /** The allowed range, inclusive; the calendar's ends without limits. */
  first: IsoDate;
  last: IsoDate;
  firstDay: Weekday;
  locale: IDateLocale;
  markers: Record<IsoDate, IColor>;
  /** The keyboard's day. Its month is the shown month, its year the shown year. */
  cursor: IsoDate;
  view: CalendarView;
  /** Moves the cursor (clamped into the range); `focus` puts focus on it after the render. */
  moveTo: (date: IsoDate | null, focus: boolean) => void;
  showView: (view: CalendarView, focus: boolean) => void;
  pick: (date: IsoDate) => void;
  /** Set by a move that should take focus; the shown grid reads and clears it. */
  focusRequest: RefObject<boolean>;
  /** The calendar's root: it holds focus while a grid is rebuilt under it. */
  rootRef: RefObject<HTMLDivElement | null>;
};
